import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedAdminRoute Component
 *
 * Restricts access to Platform Admins only.
 * Regular tenant admins will see "Access Denied" page.
 *
 * Platform Admin Check:
 * - User must be authenticated
 * - User must have role = 'admin'
 * - User must be in Platform Tenant (tenantId = '00000000-0000-0000-0000-000000000001')
 *
 * Note: Frontend check for UX only. Backend enforces security via platformAdminMiddleware.
 */
export const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is platform admin
  // Platform Admin = role='admin' AND tenantId='00000000-0000-0000-0000-000000000001'
  const isPlatformAdmin =
    user?.role === 'admin' &&
    user?.tenantId === '00000000-0000-0000-0000-000000000001';

  // Not platform admin - show access denied
  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8 bg-card rounded-lg border border-border max-w-md">
          <h2 className="text-2xl font-bold text-foreground mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-2">Platform admin access required.</p>
          <p className="text-sm text-muted-foreground">
            This area is only accessible to Mindspace Platform administrators.
          </p>
        </div>
      </div>
    );
  }

  // Platform admin - render children
  return <>{children}</>;
};
